import {ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Host, Optional, Self, ViewChild} from '@angular/core';
import {FormsModule, NgControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormField, MatFormFieldControl} from '@angular/material/form-field';
import {første} from "felles-ng2/støttefunksjoner/lister";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {BasisKlokkeslettVelgerKomponent} from "web-ng2/delt/komponenter/klokkeslett-velger/BasisKlokkeslettVelgerKomponent";
import {Temporal} from "temporal-polyfill";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {TranslateModule} from "@ngx-translate/core";
import PlainTime = Temporal.PlainTime;
import {MatTooltip} from "@angular/material/tooltip";

const debug = initDebugger('KlokkeslettVelgerKomponent');


/**
 * Input-felt for angivelse av klokkeslett som lar bruker skrive inn klokkeslett
 * samt velge fra en nedtrekksliste med predefinerte klokkeslett med 15min intervaller.
 *
 * Fungerer med Ng template-basert form ([(ngModel)]="...") og Ng reactive form ([formControl]="...").
 * Fungerer med <mat-form-field> fra Material.
 */
@Component({
    selector: 'klokkeslett-velger',
    templateUrl: 'klokkeslett-velger.html',
    styleUrls: ['klokkeslett-velger.scss'],
    imports: [
        FormsModule,
        MatInputModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        TranslateModule,
        MatTooltip
    ],
    providers: [{ provide: MatFormFieldControl, useExisting: KlokkeslettVelgerKomponent }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KlokkeslettVelgerKomponent extends BasisKlokkeslettVelgerKomponent {

    @ViewChild('tekstfelt', {static:false}) private _tekstfelt: ElementRef<HTMLInputElement> | null = null;
    @ViewChild(MatAutocomplete) private _autocomplete:MatAutocomplete | null = null;

    predefinerteKlokkeslett: PlainTime[] = this.klokkeslett15MinIntervaller();

    constructor(/** skjemakontrollen tilknyttet KlokkeslettVelgerKomponent via formControl eller ngModel direktiver*/
                @Self() tilknyttetSkjemakontroll: NgControl,
                /** mulig MatFormField som omslutter denne komponenten */
                @Optional() @Host() matFormField: MatFormField,
                destroyRef: DestroyRef,
                gjeldendeTidssone: GjeldendeTidssone) {
        super(tilknyttetSkjemakontroll, matFormField, destroyRef, gjeldendeTidssone, 'klokkeslett');
    }

    //BasisKlokkeslettVarighetKomponent impl

    override get tekstfelt(): ElementRef<HTMLInputElement> | null {
        return this._tekstfelt;
    }

    override get autocomplete(): MatAutocomplete | null {
        return this._autocomplete;
    }

    /** Finner matchende MatOption innslag fra options-listen til mat-autocomplete komponenten.
     * null dersom intet treff.
     * public for testbarhet */
    public override finnNærmesteValgAlternativ(klokkeslett: string) {
        if (erTomEllerNil(klokkeslett)) klokkeslett = '08:00'; //standard verdi dersom tom streng angitt - for å slippe å måtte scrolle så langt til første sannsynlige innslag
        const justertKlokke = KlokkeslettVelgerKomponent.justerKlokkeslettTilNærmesteKvarter(klokkeslett.trim());
        return første(this.autocomplete!.options
          .filter(kandidat => kandidat.viewValue.toLowerCase().includes(justertKlokke)));
    }

    //private hjelpemetoder

    /** Justerer angitt klokkeslett til nærmeste kvarter innenfor samme time.
     * public static for testformål */
    public static justerKlokkeslettTilNærmesteKvarter(klokkeslett: string) {
        const gyldigKlokkeslett = klokkeslett.match(KlokkeslettVelgerKomponent.klokkeslettRegexp);
        if (erNil(gyldigKlokkeslett)) return klokkeslett;

        let justertKlokkeslett = klokkeslett;
        const timer = gyldigKlokkeslett.groups!.timer;
        const minutter = gyldigKlokkeslett.groups!.minutter;
        const minutterSomTall = Number.parseInt(minutter);
        if (minutterSomTall > 37) justertKlokkeslett = `${timer}:45`;
        else if (minutterSomTall > 22) justertKlokkeslett = `${timer}:30`;
        else if (minutterSomTall > 7) justertKlokkeslett = `${timer}:15`;
        else justertKlokkeslett = `${timer}:00`;

        debug('finnNærmesteValgAlternativ justert', klokkeslett, justertKlokkeslett)
        return justertKlokkeslett;
    }

    /** Returnerer liste med kvarters-intervaller for 24 timer */
    private klokkeslett15MinIntervaller(): PlainTime[] {
        return new Array(24).fill(null)
          .flatMap((verdi, time) => new Array(4).fill(null)
            .map((verdi, minuttIndeks)=> new PlainTime(time, minuttIndeks * 15)));
    }

}
